import React, { useState, useEffect, useRef } from 'react';
import { Settings as SettingsIcon, Save, Upload, Tag, Eye, EyeOff, DollarSign, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WalletManagement from './WalletManagement';
import StripeConnectManagement from './StripeConnectManagement';
import './AgentSettings.css';

const AgentSettings = () => {
  const { user } = useAuth();
  
  // Agent settings state
  const [agentSettings, setAgentSettings] = useState({
    name: 'My Agent',
    description: 'AI agent built with Agent Builder',
    agent_avatar: 'https://example.com/avatar.png',
    tags: [],
    visibility_status: 'public',
    is_public: false,
    owner_engagement_fee_cents: 0
  });
  
  const [originalSettings, setOriginalSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Load agent settings when component mounts
  useEffect(() => {
    if (user?.agent?.id) {
      loadAgentSettings();
    }
  }, [user?.agent?.id]);

  // Update avatar when user data becomes available
  useEffect(() => {
    if (user?.picture && (!agentSettings.agent_avatar || agentSettings.agent_avatar.includes('example.com'))) {
      setAgentSettings(prev => ({
        ...prev,
        agent_avatar: user.picture
      }));
    }
  }, [user?.picture, agentSettings.agent_avatar]);

  // Generate default avatar if none exists
  const generateDefaultAvatar = (name) => {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, 200, 200);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 200, 200);
    
    // Add text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 80px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.charAt(0).toUpperCase(), 100, 100);
    
    return canvas.toDataURL();
  };

  // Load agent settings from API
  const loadAgentSettings = async () => {
    if (!user?.agent?.id) return;
    
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agents/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const agentName = data.name || 'My Agent';
        
        // Use Google profile picture as default if no avatar is set
        const defaultAvatar = data.agent_avatar || user?.picture || generateDefaultAvatar(agentName);
        
        const loadedSettings = {
          name: agentName,
          description: data.description || 'AI agent built with Agent Builder',
          agent_avatar: defaultAvatar,
          tags: data.tags || [],
          visibility_status: data.visibility_status || 'public',
          is_public: false, // Default values, will be overridden by monetization settings
          owner_engagement_fee_cents: 0
        };
        setAgentSettings(loadedSettings);
        setOriginalSettings(loadedSettings);
        
        // Load monetization settings separately
        try {
          console.log(`🔍 [AGENT_SETTINGS] Loading monetization settings for agent ${user.agent.id}...`);
          const monetizationResponse = await fetch(`/api/wallet/agent/${user.agent.id}/monetization`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log(`📡 [AGENT_SETTINGS] Monetization response status: ${monetizationResponse.status}`);
          
          if (monetizationResponse.ok) {
            const monetizationData = await monetizationResponse.json();
            console.log(`✅ [AGENT_SETTINGS] Received monetization data:`, monetizationData);
            const updatedSettings = {
              ...loadedSettings,
              is_public: monetizationData.is_public || false,
              owner_engagement_fee_cents: monetizationData.owner_engagement_fee_cents || 0
            };
            console.log(`🔄 [AGENT_SETTINGS] Updated settings with monetization data:`, updatedSettings);
            setAgentSettings(updatedSettings);
            setOriginalSettings({ ...updatedSettings });
          } else {
            const errorText = await monetizationResponse.text();
            console.error(`❌ [AGENT_SETTINGS] Monetization API error: ${monetizationResponse.status} - ${errorText}`);
          }
        } catch (monetizationError) {
          console.error('❌ [AGENT_SETTINGS] Error loading monetization settings:', monetizationError);
          // Continue with default values if monetization settings fail to load
        }
        
        // Clear any existing preview when loading settings
        setAvatarPreview(null);
        
        // If no avatar was saved and user has a Google picture, save it now
        if (!data.agent_avatar && user?.picture) {
          console.log('Saving Google profile picture as agent avatar');
          await saveGoogleProfilePicture(user.picture);
        }
        
        // If avatar is a Google URL, convert it to data URL
        if (data.agent_avatar && data.agent_avatar.includes('googleusercontent.com')) {
          console.log('Converting Google URL to data URL:', data.agent_avatar);
          try {
            await saveGoogleProfilePicture(data.agent_avatar);
            console.log('Google URL conversion completed, reloading settings...');
            // Reload settings after conversion
            setTimeout(() => {
              loadAgentSettings();
            }, 1000);
          } catch (error) {
            console.error('Error converting Google URL:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading agent settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Save Google profile picture as agent avatar
  const saveGoogleProfilePicture = async (googlePictureUrl) => {
    try {
      console.log('Attempting to convert and save Google profile picture:', googlePictureUrl);
      
      // Convert Google image to data URL
      const response = await fetch(googlePictureUrl);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const dataURL = e.target.result;
          
          try {
            const token = localStorage.getItem('token');
            const saveResponse = await fetch(`/api/agents/settings`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                ...agentSettings,
                agent_avatar: dataURL
              })
            });
            
            if (saveResponse.ok) {
              const result = await saveResponse.json();
              console.log('Google profile picture converted and saved as agent avatar:', result);
              resolve();
            } else {
              const error = await saveResponse.json();
              console.error('Failed to save Google profile picture:', error);
              reject(new Error('Failed to save'));
            }
          } catch (error) {
            console.error('Error saving Google profile picture:', error);
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting Google profile picture:', error);
    }
  };

  // Save agent settings
  const saveAgentSettings = async () => {
    if (!user?.agent?.id) return;
    
    setSettingsSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save basic agent settings
      const response = await fetch(`/api/agents/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(agentSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Agent settings saved successfully:', data);
        
        // Save monetization settings separately
        if (agentSettings.is_public !== undefined || agentSettings.owner_engagement_fee_cents !== undefined) {
          console.log(`🔍 [AGENT_SETTINGS] Saving monetization settings for agent ${user.agent.id}...`);
          console.log(`📝 [AGENT_SETTINGS] Monetization data to save:`, {
            is_public: agentSettings.is_public,
            owner_engagement_fee_cents: agentSettings.owner_engagement_fee_cents
          });
          
          const monetizationResponse = await fetch(`/api/wallet/agent/${user.agent.id}/monetization`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              is_public: agentSettings.is_public,
              owner_engagement_fee_cents: agentSettings.owner_engagement_fee_cents
            })
          });
          
          console.log(`📡 [AGENT_SETTINGS] Monetization save response status: ${monetizationResponse.status}`);
          
          if (monetizationResponse.ok) {
            const responseData = await monetizationResponse.json();
            console.log('✅ [AGENT_SETTINGS] Monetization settings saved successfully:', responseData);
          } else {
            const errorText = await monetizationResponse.text();
            console.error(`❌ [AGENT_SETTINGS] Failed to save monetization settings: ${monetizationResponse.status} - ${errorText}`);
          }
        } else {
          console.log(`ℹ️ [AGENT_SETTINGS] No monetization settings to save (is_public: ${agentSettings.is_public}, owner_engagement_fee_cents: ${agentSettings.owner_engagement_fee_cents})`);
        }
        
        // Update original settings to reflect saved state
        setOriginalSettings({ ...agentSettings });
      } else {
        const errorData = await response.json();
        console.error('Failed to save agent settings:', errorData);
        alert(`Failed to save settings: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving agent settings:', error);
      alert(`Error saving settings: ${error.message}`);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Check if settings have changed
  const hasChanges = () => {
    return JSON.stringify(agentSettings) !== JSON.stringify(originalSettings);
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setAgentSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add tag
  const addTag = () => {
    if (newTag.trim() && !agentSettings.tags.includes(newTag.trim())) {
      setAgentSettings(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setAgentSettings(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle key press for adding tags
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Handle avatar file upload
  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file (PNG, JPEG, etc.)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Create preview and convert to data URL for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataURL = e.target.result;
        setAvatarPreview(dataURL);
        // Update the agent_avatar field with the data URL
        handleInputChange('agent_avatar', dataURL);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Remove avatar
  const removeAvatar = () => {
    setAvatarPreview(null);
    handleInputChange('agent_avatar', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      

      {settingsLoading ? (
        <div className="loading-state">
          <p>Loading agent settings...</p>
        </div>
      ) : (
        <div className="settings-form">
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="basic-info-container">
                <div className="text-fields">
                  <div className="form-group">
                    <label htmlFor="agent-name">Agent Name *</label>
                    <input
                      id="agent-name"
                      type="text"
                      value={agentSettings.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter agent name"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="agent-description">Agent Description</label>
                    <input
                      id="agent-description"
                      type="text"
                      value={agentSettings.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe what your agent does"
                    />
                  </div>
                </div>
                
                {/* Avatar Section - Now on the right */}
                <div className="avatar-section">
                  <div className="avatar-preview-large">
                    {avatarPreview || agentSettings.agent_avatar ? (
                      <img 
                        src={avatarPreview || agentSettings.agent_avatar} 
                        alt="Agent Avatar Preview"
                        className="avatar-image-large"
                      />
                    ) : (
                      <div className="avatar-placeholder-large">
                        <ImageIcon className="avatar-icon-large" />
                      </div>
                    )}
                  </div>
                  <button 
                    type="button" 
                    onClick={triggerFileUpload}
                    className="edit-avatar-button"
                  >
                    <Upload className="edit-icon" />
                    Edit Avatar
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>


            {/* Discoverability & Visibility */}
            <div className="form-section">
              <h3>Discoverability & Visibility</h3>
              
              <div className="form-group">
                <label htmlFor="visibility">Visibility</label>
                <select
                  id="visibility"
                  value={agentSettings.visibility_status}
                  onChange={(e) => handleInputChange('visibility_status', e.target.value)}
                >
                  <option value="public">Public - Anyone can discover and use</option>
                  <option value="unlisted">Unlisted - Only accessible via direct link</option>
                  <option value="private">Private - Only you can access</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tags</label>
                <div className="tags-input">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Add a tag..."
                  />
                  <button type="button" onClick={addTag} className="add-tag-button">
                    <Tag className="button-icon" />
                    Add
                  </button>
                </div>
                {agentSettings.tags.length > 0 && (
                  <div className="tags-list">
                    {agentSettings.tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Monetization */}
            <div className="form-section">
              <h3>Monetization Settings</h3>
          <p className="section-description">
            Set up your agent for the marketplace. Users get 2 free messages, then pay OpenAI costs + 10%. Optionally, set a fee for your personal engagement.
          </p>
              
              <div className="form-group">
                <label htmlFor="is-public">
                  <input
                    id="is-public"
                    type="checkbox"
                    checked={agentSettings.is_public || false}
                    onChange={(e) => handleInputChange('is_public', e.target.checked)}
                  />
                  <span>Make this agent public in the marketplace</span>
                </label>
                <p className="field-description">
                  When enabled, users can discover and chat with your agent on the /talkto page.
                </p>
              </div>
              
              {agentSettings.is_public && (
                <div className="form-group">
                  <label htmlFor="owner-engagement-fee">Owner engagement fee (USD)</label>
                  <div className="price-input-container">
                    <input
                      id="owner-engagement-fee"
                      type="number"
                      value={agentSettings.owner_engagement_fee_cents ? (agentSettings.owner_engagement_fee_cents / 100) : ''}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        handleInputChange('owner_engagement_fee_cents', value * 100);
                      }}
                      placeholder="0"
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span className="currency-suffix">USD</span>
                  </div>
                  <p className="field-description">
                    Optional: Set a fee for users to request your personal review and engagement in the conversation (max $100).
                  </p>
                </div>
              )}
            </div>

            {/* Wallet & Billing */}
            <div className="form-section">
              <h3>Wallet & Billing</h3>
              <p className="section-description">
                Manage your account balance and billing information. As an agent owner, you can receive payments and manage your earnings.
              </p>
              <WalletManagement />
            </div>

            {/* Stripe Connect Account */}
            <div className="form-section">
              <h3>Payment Account</h3>
              <p className="section-description">
                Set up your Stripe Connect account to receive payments from agent interactions. You'll earn 85% of the revenue from your agents.
              </p>
              <StripeConnectManagement />
            </div>
            
            {/* Save Button */}
            <div className="form-actions">
              <button 
                className={`save-button ${!hasChanges() ? 'disabled' : ''}`}
                onClick={saveAgentSettings}
                disabled={settingsSaving || !hasChanges()}
              >
                <Save className="button-icon" />
                {settingsSaving ? 'Saving...' : hasChanges() ? 'Save Changes' : 'No Changes'}
              </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default AgentSettings;
